<script setup lang="ts">
import { computed, onMounted, reactive, ref, type Ref } from 'vue';
import CModal from '@/components/CModal.vue';
import CButtonGroup, { type CButtonGroupItem } from '@/components/shared/CButtonGroup';
import CFileInput, { type CFileInputRef } from '@/components/shared/CFileInput';
import CPagination from '@/components/shared/CPagination';
import CPreview from '@/components/shared/CPreview.vue';
import { getDefaultMasks, getUserMasks } from '@/api/masks';
import type { MaskDto } from '@/api/typings/mask';
import {
    MASK_UPLOAD_ACCEPT,
    RASTER_MASK_UPLOAD_ACCEPT,
    isSvgMask,
    uploadAndRegisterMask
} from '@/utils/maskUtils';

const PAGE_SIZE = 35;

type MaskSource = 'customily' | 'uploaded';

const props = defineProps<{
    disableSvg?: boolean;
}>();

const emit = defineEmits<{
    (event: 'select', mask: MaskDto): void;
    (event: 'cancel'): void;
    (event: 'error', message: string): void;
}>();

const sources: CButtonGroupItem<MaskSource>[] = [
    { label: 'Customily', value: 'customily' },
    { label: 'Uploaded', value: 'uploaded' }
];

const model = reactive({
    filters: [] as MaskSource[],
    defaultMasks: [] as MaskDto[],
    userMasks: [] as MaskDto[],
    userMasksTotal: 0,
    page: 1,
    loading: false,
    uploading: false
});

const fileInputRef: Ref<CFileInputRef | undefined> = ref();

const accept = computed(() => (props.disableSvg ? RASTER_MASK_UPLOAD_ACCEPT : MASK_UPLOAD_ACCEPT));
// Filters are inclusive, so none picked and both picked mean the same thing: show everything
const isFiltered = (source: MaskSource) => !model.filters.length || model.filters.includes(source);
const showUserMasks = computed(() => isFiltered('uploaded'));
const showDefaultMasks = computed(() => isFiltered('customily'));

const userMasksCount = computed(() => (showUserMasks.value ? model.userMasksTotal : 0));
const totalMaskCount = computed(
    () => userMasksCount.value + (showDefaultMasks.value ? model.defaultMasks.length : 0)
);

// Unfiltered it is one flat list of the user's masks followed by the Customily ones: the defaults
// start filling the page where the uploaded ones run out and spill onto the next ones
const masks = computed(() => {
    const userMasks = showUserMasks.value ? model.userMasks : [];
    if (!showDefaultMasks.value) return userMasks;

    const firstDefault = Math.max(0, (model.page - 1) * PAGE_SIZE - userMasksCount.value);
    return [
        ...userMasks,
        ...model.defaultMasks.slice(firstDefault, firstDefault + PAGE_SIZE - userMasks.length)
    ];
});
const isEmpty = computed(() => !model.loading && !masks.value.length);
const emptyMessage = computed(() => {
    if (!showDefaultMasks.value) {
        return "You haven't uploaded any shape yet. Upload one and it will be available on every template.";
    }
    if (!showUserMasks.value) return 'There are no Customily shapes available yet.';
    return 'There are no shapes available yet. Upload one and it will be available on every template.';
});

const loadDefaultMasks = async () => {
    model.defaultMasks = await getDefaultMasks();
};

const loadUserMasks = async (page: number) => {
    const { masks: userMasks, totalCount } = await getUserMasks(page, PAGE_SIZE);
    model.userMasks = userMasks;
    model.userMasksTotal = totalCount;
    model.page = page;
};

const withLoading = async (loading: Promise<unknown>) => {
    model.loading = true;
    try {
        await loading;
    } finally {
        model.loading = false;
    }
};

const onPageChange = (page: number) => withLoading(loadUserMasks(page));

const onFiltersChange = (filters: MaskSource[]) => {
    model.filters = filters;
    if (model.page !== 1) onPageChange(1);
};

const isDisabled = (mask: MaskDto) => props.disableSvg && isSvgMask(mask.path);

const onMaskSelected = (mask: MaskDto) => {
    if (isDisabled(mask)) return;
    emit('select', mask);
};

const onUploadClicked = () => fileInputRef.value?.openFileUploader();

const onFileUpload = async (files: File[]) => {
    const file = files[0];
    if (!file) return;

    model.uploading = true;
    try {
        const mask = await uploadAndRegisterMask(file);
        emit('select', mask);
    } catch (error) {
        emit('error', (error as Error).message || 'The mask could not be uploaded');
    } finally {
        model.uploading = false;
    }
};

onMounted(() => withLoading(Promise.all([loadDefaultMasks(), loadUserMasks(1)])));
</script>

<template>
    <c-modal :value="true" hide-footer size="lg" title="Select or Upload a Shape" @closed="emit('cancel')">
        <div class="mask-selector">
            <span class="filter-label">Filter Shapes</span>
            <c-button-group
                :value="model.filters"
                :items="sources"
                type="checkbox"
                size="sm"
                pill
                separate
                no-icon
                @input="onFiltersChange"
            />

            <div class="mask-grid">
                <button
                    type="button"
                    class="mask-tile upload-tile"
                    :disabled="model.uploading"
                    @click="onUploadClicked"
                >
                    <font-awesome-icon v-if="model.uploading" class="fa-spin" :icon="['fas', 'spinner-third']" />
                    <template v-else>
                        <font-awesome-icon :icon="['far', 'arrow-up-from-bracket']" />
                        <span>Upload</span>
                    </template>
                </button>

                <button
                    v-for="mask of masks"
                    :key="mask.id"
                    type="button"
                    class="mask-tile"
                    :class="{ disabled: isDisabled(mask) }"
                    :title="isDisabled(mask) ? 'SVG masks cannot be used with an initial image position' : mask.name"
                    @click="onMaskSelected(mask)"
                >
                    <c-preview :src="mask.thumbnailPath" :alt="mask.name" hide-wrapper max-height="100%" />
                </button>
            </div>

            <p v-if="isEmpty" class="empty-message">{{ emptyMessage }}</p>

            <c-pagination
                v-if="totalMaskCount > PAGE_SIZE"
                :value="model.page"
                :total-count="totalMaskCount"
                :page-count="PAGE_SIZE"
                @input="onPageChange"
            />

            <c-file-input
                ref="fileInputRef"
                class="d-none"
                :value="[]"
                :accept="accept"
                :display-preview="false"
                @input="onFileUpload"
            />
        </div>
    </c-modal>
</template>

<style lang="scss" scoped>
@import 'styles/utilities/functions';

.mask-selector {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.filter-label {
    color: get-color('gray-500');
    font-size: 0.875rem;
}

.mask-grid {
    display: grid;
    grid-template-columns: repeat(9, minmax(0, 1fr));
    gap: 0.5rem;
}

.mask-tile {
    aspect-ratio: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
    padding: 0.5rem;
    background: get-color('white');
    border: 1px solid get-color('gray-200');
    border-radius: 0.25rem;
    font-size: 0.75rem;
    color: get-color('gray-600');

    &:hover:not(.disabled) {
        border-color: get-color('purple');
    }

    &.disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }

    img {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
    }
}

.upload-tile:disabled {
    cursor: progress;
}

.empty-message {
    margin: 0;
    color: get-color('gray-500');
    text-align: center;
}
</style>
